// ---------------------------------------------------------------------------
// Flow Diagram — Serialize/deserialize ActionFlow diagram layout.
//
// Persists node positions, connections, and viewport to flow.cogo.json's
// `_diagram` field so diagram layout survives editor close/reopen.
// ---------------------------------------------------------------------------

import type { ActionFlowNode, ActionFlowConnection } from '../models/flow-models'

/** Persisted diagram data for a single flow. */
export interface FlowDiagramData {
  nodes: PersistedNode[]
  connections: PersistedConnection[]
  viewport?: { x: number; y: number; zoom: number }
}

/** Node data stripped of transient state (executionStatus, executionDetail). */
export interface PersistedNode {
  id: string
  nodeType: string
  actionType?: string
  label: string
  position: { x: number; y: number }
  parameters?: Record<string, unknown>
  connections?: Record<string, string | null>
  childNodeIds?: string[]
  isCollapsed?: boolean
}

/** Connection (no transient fields). */
export interface PersistedConnection {
  id: string
  sourceNodeId: string
  targetNodeId: string
  connectionType: string
  outputIndex?: number
}

/** Serialize canvas state to persistable diagram data. */
export function serializeDiagram(
  nodes: ActionFlowNode[],
  connections: ActionFlowConnection[],
  viewport?: { x: number; y: number; zoom: number },
): FlowDiagramData {
  return {
    nodes: nodes.map((n) => ({
      id: n.id,
      nodeType: n.nodeType,
      ...(n.actionType && { actionType: n.actionType }),
      label: n.label,
      position: { ...n.position },
      ...(n.parameters && Object.keys(n.parameters).length > 0 && { parameters: n.parameters }),
      ...(n.connections && Object.keys(n.connections).length > 0 && { connections: n.connections }),
      ...(n.childNodeIds?.length && { childNodeIds: n.childNodeIds }),
      ...(n.isCollapsed && { isCollapsed: n.isCollapsed }),
      // Excluded: executionStatus, executionDetail (transient runtime state)
    })),
    connections: connections.map((c) => ({
      id: c.id,
      sourceNodeId: c.sourceNodeId,
      targetNodeId: c.targetNodeId,
      connectionType: c.connectionType,
      ...(c.outputIndex !== undefined && c.outputIndex !== 0 && { outputIndex: c.outputIndex }),
    })),
    ...(viewport && { viewport }),
  }
}

/** Deserialize persisted diagram data to canvas state. */
export function deserializeDiagram(
  data: FlowDiagramData,
): { nodes: ActionFlowNode[]; connections: ActionFlowConnection[] } {
  const nodes: ActionFlowNode[] = data.nodes.map((n) => ({
    id: n.id,
    nodeType: n.nodeType as ActionFlowNode['nodeType'],
    actionType: n.actionType ?? '',
    label: n.label,
    position: { ...n.position },
    parameters: n.parameters ?? {},
    connections: (n.connections ?? {}) as ActionFlowNode['connections'],
    childNodeIds: n.childNodeIds ?? [],
    isCollapsed: n.isCollapsed ?? false,
    // Restore transient defaults
    executionStatus: 'idle' as const,
    executionDetail: undefined,
  }))

  const connections: ActionFlowConnection[] = data.connections.map((c) => ({
    id: c.id,
    sourceNodeId: c.sourceNodeId,
    targetNodeId: c.targetNodeId,
    connectionType: c.connectionType as ActionFlowConnection['connectionType'],
    outputIndex: c.outputIndex ?? 0,
  }))

  return { nodes, connections }
}
